import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ProfessorAssignment } from '../../interfaces/professor';
import { ProfessorService } from '../professor.service';
import { StudentService } from '../student.service';
import { HandleErrorsService } from './handle-errors.service';
import { Student, StudentAssignment } from '../../interfaces/student';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private assignmentsSubject = new BehaviorSubject<ProfessorAssignment[]>([]);
  assignments$ = this.assignmentsSubject.asObservable();

  private studentAssignmentsSubject = new BehaviorSubject<StudentAssignment[]>(
    [],
  );
  studentAssignments$ = this.studentAssignmentsSubject.asObservable();

  private cachedUser: any = null;
  private cachedStudentId: number | null = null;
  private cachedStudent: Student | null = null;

  constructor(
    private keycloak: KeycloakService,
    private professorService: ProfessorService,
    private studentService: StudentService,
    private errorHandler: HandleErrorsService,
  ) {}

  /**
   * Identité de l'utilisateur connecté, lue **dans le jeton**.
   *
   * <p>Auparavant via `loadUserProfile()`, donc l'API Account de Keycloak :
   * un aller-retour réseau soumis au CORS, pour des champs que le jeton
   * transporte déjà. Un realm dont les origines web ne sont pas déclarées
   * faisait échouer l'appel et, l'initialiseur rejetant, l'application ne
   * démarrait pas du tout.</p>
   */
  async getUser(): Promise<any> {
    if (this.cachedUser) return this.cachedUser;

    // `getToken()` ne renouvelle rien : il rend le jeton en mémoire, tel
    // quel. Les claims lus ici sont ceux du dernier jeton reçu — l'identité
    // (nom, adresse) ne change pas d'un jeton à l'autre, et c'est
    // l'intercepteur qui tient le jeton à jour (`SessionTokenService`).
    await this.keycloak.getToken();
    const claims: any = this.keycloak.getKeycloakInstance().tokenParsed;
    if (!claims) return null;

    this.cachedUser = {
      id: claims.sub,
      username: claims.preferred_username,
      email: claims.email,
      firstName: claims.given_name,
      lastName: claims.family_name,
      fullName: claims.name,
    };
    return this.cachedUser;
  }

  async getUserId(): Promise<string | null> {
    const user = await this.getUser();
    return user?.id || null;
  }

  /**
   * Get student ID from backend using Keycloak user ID
   */
  async getStudentId(): Promise<number | null> {
    // Return cached value if available
    if (this.cachedStudentId !== null) {
      return this.cachedStudentId;
    }

    // Check localStorage cache
    const cached = localStorage.getItem('student_id');
    if (cached) {
      this.cachedStudentId = Number(cached);
      return this.cachedStudentId;
    }

    // Fetch from backend
    const userId = await this.getUserId();
    if (!userId) {
      console.error('No user ID available');
      return null;
    }

    try {
      const response = await firstValueFrom(
        this.studentService.getStudentByUserId(userId),
      );

      if (response.status === 'SUCCESS' && response.data) {
        this.cachedStudentId = response.data.id;
        // Cache it in localStorage
        localStorage.setItem('student_id', String(this.cachedStudentId));
        console.log('✅ Student ID cached:', this.cachedStudentId);
        return this.cachedStudentId;
      } else {
        console.error('Failed to get student data:', response.message);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching student ID:', error);
      this.errorHandler.handle('SWAL.FETCH_USER_ERROR');
      return null;
    }
  }

  /**
   * Get the full Student record (incl. birthDate, learningPathId, …) using
   * the Keycloak user id. Result is cached for the session.
   */
  async getStudent(): Promise<Student | null> {
    if (this.cachedStudent) return this.cachedStudent;

    const userId = await this.getUserId();
    if (!userId) return null;

    try {
      const response = await firstValueFrom(
        this.studentService.getStudentByUserId(userId),
      );
      if (response.status === 'SUCCESS' && response.data) {
        this.cachedStudent = response.data;
        // Keep id cache aligned for callers that only need the id
        this.cachedStudentId = response.data.id;
        localStorage.setItem('student_id', String(this.cachedStudentId));
        return this.cachedStudent;
      }
      return null;
    } catch (error) {
      console.error('❌ Error fetching student record:', error);
      return null;
    }
  }

  /**
   * Get the user's display name (firstname + lastname or username as fallback)
   */
  async getUserDisplayName(): Promise<string> {
    const user = await this.getUser();

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.lastName) {
      return user.lastName;
    } else if (user.username) {
      return user.username;
    }

    return 'User';
  }

  /**
   * Get user's email
   */
  async getUserEmail(): Promise<string | null> {
    const user = await this.getUser();
    return user?.email || null;
  }

  /**
   * Get user's avatar URL from Keycloak attributes (if stored)
   */
  async getUserAvatar(): Promise<string> {
    const user = await this.getUser();
    return user?.attributes?.avatar?.[0] || 'assets/images/default-user.png';
  }

  async getToken(): Promise<string> {
    return await this.keycloak.getToken();
  }

  getRoles(): string[] {
    const tokenParsed = this.keycloak.getKeycloakInstance().tokenParsed;
    return tokenParsed?.realm_access?.roles || [];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  /**
   * Permissions du porteur du jeton.
   *
   * <p>Une permission est un <b>rôle de client</b> Keycloak — `sessions.inspect`,
   * `library.read.all` — porté par `skill2era-api`. `getRoles()` ne lit que
   * `realm_access` : il ne les voit pas, et c'est pour cela qu'il ne suffit
   * pas.</p>
   *
   * <p>Tous les clients sont fusionnés plutôt que de nommer `skill2era-api` :
   * un contrôle d'accès qui dépendrait de l'identifiant du client casserait le
   * jour où celui-ci est renommé.</p>
   */
  getPermissions(): string[] {
    const parsed = this.keycloak.getKeycloakInstance().tokenParsed as
      | { resource_access?: Record<string, { roles?: string[] }> }
      | undefined;

    return Object.values(parsed?.resource_access ?? {}).flatMap((c) => c?.roles ?? []);
  }

  /**
   * Le superviseur a tout, par décision — pas seulement par son composite
   * Keycloak : une permission ajoutée au catalogue et pas encore au composite
   * ne le bloque pas. Même règle que `RequirePermission` côté serveur.
   */
  hasPermission(permission: string): boolean {
    return this.hasRole('supervisor') || this.getPermissions().includes(permission);
  }

  isTeacher(): boolean {
    return this.hasRole('professor');
  }

  /**
   * L'équipe d'administration : le superviseur, ou un administrateur muni d'un
   * profil. Les écrans partagés qui s'adaptent à « l'admin » (parcours sans
   * borne, audience de l'aide, choix du propriétaire à l'import) le font
   * pour les deux — c'est le profil qui dit ensuite ce qu'ils ouvrent.
   */
  isAdmin(): boolean {
    return this.hasRole('supervisor') || this.hasRole('admin');
  }

  isStudent(): boolean {
    return this.hasRole('student');
  }

  async loadAssignments(): Promise<void> {
    // Check if assignments exist in localStorage
    const cached = localStorage.getItem('professor_assignments');
    if (cached) {
      const assignments = JSON.parse(cached) as ProfessorAssignment[];
      this.assignmentsSubject.next(assignments);
      return;
    }

    // If not cached, fetch from API
    const professorId = await this.getUserId();
    if (!professorId) return;

    this.professorService.getAssignmentsByProfessor(professorId).subscribe({
      next: (res) => {
        const assignments = res.data;
        localStorage.setItem(
          'professor_assignments',
          JSON.stringify(assignments),
        );
        this.assignmentsSubject.next(assignments);
      },
      error: () => {
        this.errorHandler.handle('SWAL.FETCH_USER_ERROR');
      },
    });
  }

  logout(): void {
    localStorage.removeItem('professor_assignments');
    localStorage.removeItem('student_id');
    this.cachedUser = null;
    this.cachedStudentId = null;
    this.cachedStudent = null;
    this.keycloak.logout(window.location.origin);
  }
}
